import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SectionTitle, LuxuryTable } from '@/components/Luxury';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { MetricLabel } from './MetricLabel';

/**
 * Projection data for a single year in the 10-year analysis.
 */
export interface YearProjection {
  year: number;
  noi: number;
  debtService: number;
  preTaxCF: number;
  taxBenefit: number;
  afterTaxCF: number;
  afterTaxCoC: number;  // as decimal (e.g., 0.125 for 12.5%)
  exitIrr?: number | null;         // After-tax exit IRR if sold at end of this year (includes depreciation shields + exit taxes)
  afterTaxIrr?: number | null;     // After-tax exit IRR if sold at end of this year (Phase 83)
  cumulativeCF?: number | null;    // Pre-tax cumulative cash flow (secondary — muted)
  afterTaxCumulativeCF?: number | null; // After-tax cumulative cash flow (primary — bold emerald)
  // Exit tax breakdown (Phase 120)
  exitRecaptureTax?: number | null;
  exitCapitalGainsTax?: number | null;
  exitStateTax?: number | null;
  exitTotalTaxes?: number | null;
  exitAfterTaxNetProceeds?: number | null;
  exitEquityMultiple?: number | null;    // Backend equity multiple (includes exit proceeds)
  loanBalance?: number | null;     // Remaining loan balance at year end
  salePrice?: number | null;       // Property sale price if sold at year end
}

export interface TenYearTableProps {
  projections: YearProjection[];
  zandiProjections?: any[];  // Optional Zandi-adjusted projections (backend format with snake_case)
  equityRequired?: number;   // Initial equity investment (for Year 0 row + equity multiple column)
}

/**
 * TenYearTable component - Displays 10-year investment projections.
 *
 * Features:
 * - Luxury dark theme styling with LuxuryTable
 * - Year 5 row highlighted with amber background (recommended exit year)
 * - After-tax CoC column in emerald color
 * - JetBrains Mono for all numeric values
 * - Sticky header for scroll
 * - Mobile responsive with horizontal scroll
 *
 * Reference: sumac_offmarket_analysis.html 10-year section
 */
export function TenYearTable({ projections, zandiProjections, equityRequired }: TenYearTableProps) {
  const [showExitTax, setShowExitTax] = useState(true);

  if (!projections || projections.length === 0) {
    return null;
  }

  // Check if Zandi columns should be shown
  const showZandiColumns = zandiProjections && zandiProjections.length > 0;

  // Check if any projection has exit tax data
  const hasExitTaxData = projections.some(p => (p.exitTotalTaxes ?? 0) > 0);

  return (
    <div className="space-y-4">
      <SectionTitle>10-Year Investment Projections</SectionTitle>

      <div className="overflow-x-auto rounded-lg border border-slate-dark">
        <LuxuryTable>
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="!text-center w-16">Year</th>
              <th className="!text-right">NOI</th>
              {showZandiColumns && (
                <th className="!text-right border-l-2 border-gold/30 bg-gold/5">NOI (Zandi)</th>
              )}
              <th className="!text-right">Debt Service</th>
              <th className="!text-right">Pre-tax CF</th>
              {showZandiColumns && (
                <th className="!text-right border-l-2 border-gold/30 bg-gold/5">CF (Zandi)</th>
              )}
              <th className="!text-right">Tax Benefit</th>
              <th className="!text-right">After-tax CF</th>
              <th className="!text-right"><MetricLabel metric="after_tax_coc">After-tax CoC</MetricLabel></th>
              <th className="!text-right"><MetricLabel metric="irr">Exit IRR (AT)</MetricLabel></th>
              {showZandiColumns && (
                <th className="!text-right border-l-2 border-gold/30 bg-gold/5">IRR (Zandi)</th>
              )}
              <th className="!text-right">Loan Balance</th>
              <th className="!text-right">Sale Price</th>
              <th className="!text-right text-stone-700 text-sm">Pre-Tax Cumul</th>
              <th className="!text-right font-semibold">After-Tax Cumul</th>
              {equityRequired != null && equityRequired > 0 && (
                <th className="!text-right"><MetricLabel metric="equity_multiple">Eq Multiple</MetricLabel></th>
              )}
            </tr>
          </thead>
          <tbody>
            {/* Year 0 — initial equity investment */}
            {equityRequired != null && equityRequired > 0 && (() => {
              const extraColsY0 = (showZandiColumns ? 3 : 0);
              // Span covers: NOI, (Zandi NOI), Debt Service, Pre-tax CF, (Zandi CF), Tax Benefit, After-tax CF, After-tax CoC, Exit IRR, (Zandi IRR), Loan Balance, Sale Price
              const spanCols = 9 + extraColsY0; // all data columns between Year and cumulative cols
              return (
                <tr className="text-stone-700 italic border-b border-slate-dark/50">
                  <td className="!text-center font-mono">0</td>
                  <td colSpan={spanCols} className="num text-stone-700 text-sm">
                    Initial equity investment
                  </td>
                  <td className="num text-crimson font-mono">—</td>
                  <td className="num text-crimson font-mono font-semibold">
                    {formatCurrency(-equityRequired)}
                  </td>
                  <td className="num text-stone-700 font-mono">0.00x</td>
                </tr>
              );
            })()}
            {projections.map((row) => {
              const isYear5 = row.year === 5;
              // Find matching Zandi projection row (backend uses snake_case)
              const zandiRow = showZandiColumns
                ? zandiProjections?.find((zp: any) => zp.year === row.year)
                : null;

              return (
                <tr
                  key={row.year}
                  className={
                    isYear5
                      ? 'bg-amber-900/20 !font-semibold [&_td]:!text-amber-100'
                      : ''
                  }
                >
                  {/* Year */}
                  <td className="!text-center font-mono">
                    {row.year}
                  </td>

                  {/* NOI */}
                  <td className="num">
                    {formatCurrency(row.noi)}
                  </td>

                  {/* NOI (Zandi) */}
                  {showZandiColumns && (
                    <td className="num border-l-2 border-gold/30 bg-gold/5 text-gold">
                      {zandiRow ? formatCurrency(zandiRow.noi) : '--'}
                    </td>
                  )}

                  {/* Debt Service */}
                  <td className="num">
                    {formatCurrency(row.debtService)}
                  </td>

                  {/* Pre-tax CF */}
                  <td className="num">
                    {formatCurrency(row.preTaxCF)}
                  </td>

                  {/* CF (Zandi) */}
                  {showZandiColumns && (
                    <td className="num border-l-2 border-gold/30 bg-gold/5 text-gold">
                      {zandiRow ? formatCurrency(zandiRow.cash_flow) : '--'}
                    </td>
                  )}

                  {/* Tax Benefit */}
                  <td className="num text-emerald">
                    {formatCurrency(row.taxBenefit)}
                  </td>

                  {/* After-tax CF */}
                  <td className="num">
                    {isNaN(row.afterTaxCF) ? '$0' : formatCurrency(row.afterTaxCF)}
                  </td>

                  {/* After-tax CoC - emerald color (with NaN guard) */}
                  <td className={`num font-semibold ${isYear5 ? '!text-amber-100' : 'text-emerald'}`}>
                    {isNaN(row.afterTaxCoC) ? '0.0%' : formatPercent(row.afterTaxCoC, 1)}
                  </td>

                  {/* Exit IRR (after-tax: depreciation shields + exit taxes) */}
                  <td className="num">
                    {row.exitIrr != null ? formatPercent(row.exitIrr, 1) : '--'}
                  </td>

                  {/* IRR (Zandi) */}
                  {showZandiColumns && (
                    <td className="num border-l-2 border-gold/30 bg-gold/5 text-gold">
                      {zandiRow?.exit_irr != null ? formatPercent(zandiRow.exit_irr, 1) : '--'}
                    </td>
                  )}

                  {/* Loan Balance */}
                  <td className="num text-stone-700">
                    {row.loanBalance != null ? formatCurrency(row.loanBalance) : '--'}
                  </td>

                  {/* Sale Price */}
                  <td className="num text-ivory">
                    {row.salePrice != null ? formatCurrency(row.salePrice) : '--'}
                  </td>

                  {/* Pre-tax cumulative CF (secondary — muted stone) */}
                  <td className={`num text-sm ${isYear5 ? '' : 'text-stone-700'}`}>
                    {row.cumulativeCF != null ? formatCurrency(row.cumulativeCF) : '--'}
                  </td>

                  {/* After-tax cumulative CF (PRIMARY — bold, emerald/red color-coded) */}
                  <td className={`num font-semibold ${
                    isYear5
                      ? '!text-amber-100'
                      : (row.afterTaxCumulativeCF ?? 0) >= 0
                        ? 'text-emerald'
                        : 'text-crimson'
                  }`}>
                    {row.afterTaxCumulativeCF != null
                      ? formatCurrency(row.afterTaxCumulativeCF)
                      : row.cumulativeCF != null
                        ? formatCurrency(row.cumulativeCF)
                        : '--'}
                  </td>

                  {/* Equity Multiple — use backend exit_equity_multiple (includes exit proceeds) when available,
                      fall back to cumulative CF-only multiple for intermediate years */}
                  {equityRequired != null && equityRequired > 0 && (() => {
                    const cumCF = row.afterTaxCumulativeCF ?? row.cumulativeCF ?? 0;
                    // Backend exit_equity_multiple includes exit proceeds; CF-only is fallback
                    const multiple = row.exitEquityMultiple
                      ?? ((equityRequired + cumCF) / equityRequired);
                    const isFullMultiple = row.exitEquityMultiple != null;
                    return (
                      <td className={`num font-mono ${
                        isYear5
                          ? '!text-amber-100 font-semibold'
                          : isFullMultiple && multiple >= 1
                            ? 'text-ivory'
                            : 'text-stone-700'
                      }`}>
                        {multiple.toFixed(2)}x
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
          {/* Tax Savings summary footer */}
          {(() => {
            const y5 = projections.find(p => p.year === 5);
            const lastProjection = projections[projections.length - 1];
            const y10 = projections.find(p => p.year === 10) ?? lastProjection;
            const hasData = (y5?.cumulativeCF != null || y5?.afterTaxCumulativeCF != null)
                         || (y10?.cumulativeCF != null || y10?.afterTaxCumulativeCF != null);
            if (!hasData) return null;

            const taxDelta5  = (y5?.afterTaxCumulativeCF ?? 0) - (y5?.cumulativeCF ?? 0);
            const taxDelta10 = (y10?.afterTaxCumulativeCF ?? 0) - (y10?.cumulativeCF ?? 0);
            const holdYear   = lastProjection?.year ?? 10;
            const taxDeltaLabel5  = taxDelta5  >= 0 ? `+${formatCurrency(taxDelta5)}`  : formatCurrency(taxDelta5);
            const taxDeltaLabel10 = taxDelta10 >= 0 ? `+${formatCurrency(taxDelta10)}` : formatCurrency(taxDelta10);

            // Compute total column span before the two cumulative columns
            const extraCols = showZandiColumns ? 3 : 0;
            const baseCols  = 10; // Year, NOI, Debt Svc, Pre-tax CF, Tax Benefit, After-tax CF, After-tax CoC, Exit IRR, Loan Balance, Sale Price
            const totalSpan = baseCols + extraCols;

            const hasEqMultCol = equityRequired != null && equityRequired > 0;

            // After-Tax IRR values (only computed for years 5 and 10)
            const atIrr5 = y5?.afterTaxIrr;
            const atIrr10 = y10?.afterTaxIrr;
            const hasAfterTaxIrr = atIrr5 != null || atIrr10 != null;

            return (
              <tfoot>
                <tr className="border-t border-stone-700">
                  <td colSpan={totalSpan} className="py-2 pl-3 text-amber-400 font-medium text-sm">
                    Tax Savings (Cumulative)
                  </td>
                  {y5 && (
                    <>
                      <td className="py-2 text-right text-sm text-stone-700 pr-2">
                        5yr: <span className={taxDelta5 >= 0 ? 'text-emerald font-semibold' : 'text-crimson font-semibold'}>
                          {taxDeltaLabel5}
                        </span>
                      </td>
                      <td className="py-2 text-right text-sm pr-3">
                        {holdYear !== 5 ? (
                          <span className={taxDelta10 >= 0 ? 'text-emerald font-semibold' : 'text-crimson font-semibold'}>
                            {holdYear}yr: {taxDeltaLabel10}
                          </span>
                        ) : null}
                      </td>
                    </>
                  )}
                  {!y5 && (
                    <>
                      <td />
                      <td className="py-2 text-right text-sm pr-3">
                        <span className={taxDelta10 >= 0 ? 'text-emerald font-semibold' : 'text-crimson font-semibold'}>
                          {holdYear}yr: {taxDeltaLabel10}
                        </span>
                      </td>
                    </>
                  )}
                  {hasEqMultCol && <td />}
                </tr>
                {hasAfterTaxIrr && (
                  <tr className="border-t border-stone-700/50">
                    <td colSpan={totalSpan} className="py-2 pl-3 text-amber-400 font-medium text-sm">
                      After-Tax IRR (Exit)
                    </td>
                    <td className="py-2 text-right text-sm pr-2">
                      {atIrr5 != null ? (
                        <>5yr: <span className={`font-semibold ${atIrr5 >= 0.15 ? 'text-emerald' : atIrr5 >= 0.12 ? 'text-amber-400' : 'text-crimson'}`}>
                          {formatPercent(atIrr5, 1)}
                        </span></>
                      ) : null}
                    </td>
                    <td className="py-2 text-right text-sm pr-3">
                      {atIrr10 != null ? (
                        <>10yr: <span className={`font-semibold ${atIrr10 >= 0.15 ? 'text-emerald' : atIrr10 >= 0.12 ? 'text-amber-400' : 'text-crimson'}`}>
                          {formatPercent(atIrr10, 1)}
                        </span></>
                      ) : null}
                    </td>
                    {hasEqMultCol && <td />}
                  </tr>
                )}
              </tfoot>
            );
          })()}
        </LuxuryTable>
      </div>

      {/* Footer note */}
      <p className="text-sm text-stone-700">
        Year 5 highlighted as recommended exit point. After-tax CoC and Exit IRR (AT) include depreciation tax benefits and exit taxes.
        {showZandiColumns && ' Zandi columns show submarket momentum-adjusted projections.'}
      </p>

      {/* Exit Tax Analysis — collapsible, only when data exists */}
      {hasExitTaxData && (
        <div className="mt-4">
          <button
            onClick={() => setShowExitTax(!showExitTax)}
            className="flex items-center gap-2 text-sm text-amber-400 hover:text-gold transition-colors font-medium"
          >
            {showExitTax ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Exit Tax Analysis by Hold Period
          </button>

          {showExitTax && (
            <div className="overflow-x-auto rounded-lg border border-slate-dark mt-3">
              <LuxuryTable>
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="!text-center w-16">Year</th>
                    <th className="!text-right">Recapture Tax</th>
                    <th className="!text-right">Capital Gains Tax</th>
                    <th className="!text-right">State Tax</th>
                    <th className="!text-right font-semibold">Total Exit Taxes</th>
                    <th className="!text-right font-semibold">After-Tax Net Proceeds</th>
                  </tr>
                </thead>
                <tbody>
                  {projections
                    .filter(row => (row.exitTotalTaxes ?? 0) > 0)
                    .map(row => {
                      const isYear5 = row.year === 5;
                      return (
                        <tr
                          key={row.year}
                          className={
                            isYear5
                              ? 'bg-amber-900/20 !font-semibold [&_td]:!text-amber-100'
                              : ''
                          }
                        >
                          <td className="!text-center font-mono">{row.year}</td>
                          <td className="num text-crimson">
                            {row.exitRecaptureTax != null ? formatCurrency(row.exitRecaptureTax) : '--'}
                          </td>
                          <td className="num text-crimson">
                            {row.exitCapitalGainsTax != null ? formatCurrency(row.exitCapitalGainsTax) : '--'}
                          </td>
                          <td className="num text-crimson">
                            {row.exitStateTax != null ? formatCurrency(row.exitStateTax) : '--'}
                          </td>
                          <td className="num font-semibold text-crimson">
                            {row.exitTotalTaxes != null ? formatCurrency(row.exitTotalTaxes) : '--'}
                          </td>
                          <td className={`num font-semibold ${isYear5 ? '!text-amber-100' : 'text-emerald'}`}>
                            {row.exitAfterTaxNetProceeds != null ? formatCurrency(row.exitAfterTaxNetProceeds) : '--'}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </LuxuryTable>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
